import { memo, useMemo, useState } from 'react';
import { PlatformBrandKitFile } from '@goldcast/player-config/src/types/brand-kit-types';
import { appendActiveVideoIfMissing } from './utils';
import { AddSegmentType } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/IntroOutroPopover/types';
import { VIDEO_SEGMENT_MAX_FILE_SIZE } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/constants';
import BrandKitMediaCard from '@/Pages/BrandKit/components/MediaCard/BrandKitMediaCard';
import MediaEmptyState from '@/Pages/BrandKit/components/MediaCard/MediaEmptyState';
import MediaPickerModal from '@/Pages/BrandKit/components/MediaCard/MediaPickerModal';
import MediaPickerTabBar from '@/Pages/BrandKit/components/MediaCard/MediaPickerTabBar';
import BrandKitSelector from '@/Pages/BrandKit/components/BrandKitSelector/BrandKitSelector';
import FilesInput from '@/components/atoms/inputs/FilesInput/FilesInput';
import useBrandKitComponent from '@/Pages/BrandKit/components/useBrandKitComponent';
import { getBrandKitUrl, getMediaPickerConfirmDisabled } from '@/Pages/BrandKit/components/utils';
import { showErrorToast } from '@/libs/toast/toast';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { updateIntroOutro } from '@/stores/clip';
import { useMediaPickerState } from '@/Pages/BrandKit/components/MediaCard/useMediaPickerState';
import { BRAND_KIT_ACCEPT_FORMATS, MEDIA_PICKER_TABS } from '@/Pages/BrandKit/constants';

interface IntroOutroModalProps {
  isOpen: boolean;
  onClose: () => void;
  type?: AddSegmentType;
  onVideoSelect?: (url: string, duration: number) => void;
  title?: string;
}

function IntroOutroModal({
  isOpen,
  onClose,
  type,
  onVideoSelect,
  title = 'Add Intro/Outro Videos'
}: IntroOutroModalProps) {
  const { clipId, clipData } = useClipsContext();
  const [selectedUrl, setSelectedUrl] = useState<string | undefined>();
  const [durations, setDurations] = useState<Record<string, number>>({});

  const { clipsBrandKitStore, onFilesUpload, deleteItem } = useBrandKitComponent({
    key: 'intro_outro',
    title: 'Intro/Outro Videos',
    clipId: clipData.id,
    maxFileSize: VIDEO_SEGMENT_MAX_FILE_SIZE
  });

  const { activeTab, setActiveTab, activeBrandKitId, setActiveBrandKitId, brandKits, activeKit, showUploadsOnly } =
    useMediaPickerState();

  const brandKitVideos = useMemo(
    () => ((activeKit as any)?.intro_outro as PlatformBrandKitFile[] | undefined) ?? [],
    [activeKit]
  );

  const recentlyUsedVideos = useMemo(() => {
    const list = (clipsBrandKitStore[clipId]?.intro_outro ?? []).map(name => ({
      name,
      url: getBrandKitUrl(name, clipData.id),
      deletable: true
    }));

    appendActiveVideoIfMissing(list, clipData.asset_metadata.intro);
    appendActiveVideoIfMissing(list, clipData.asset_metadata.outro);
    return list;
  }, [clipsBrandKitStore, clipId, clipData.id, clipData.asset_metadata.intro, clipData.asset_metadata.outro]);

  const recentlyUsedVideoUrls = useMemo(() => recentlyUsedVideos.map(({ url }) => url), [recentlyUsedVideos]);

  const confirmDisabled = useMemo(
    () =>
      getMediaPickerConfirmDisabled({
        selectedUrl,
        activeTab,
        brandKitMedia: brandKitVideos,
        uploadsMediaUrls: recentlyUsedVideoUrls
      }) ||
      (!!selectedUrl && !(selectedUrl in durations)),
    [selectedUrl, activeTab, brandKitVideos, recentlyUsedVideoUrls, durations]
  );

  function handleBrandKitChange(brandKitId: string) {
    setActiveBrandKitId(brandKitId);
    setSelectedUrl(undefined);
  }

  function handleDurationAvailable(url: string, duration: number) {
    setDurations(prev => ({ ...prev, [url]: duration }));
  }

  function handleUpload(files: File[]) {
    onFilesUpload(files).catch(() =>
      showErrorToast(
        `Oops! Uploading your intro/outro encountered an issue. Please make sure your file is in MP4 format and doesn't exceed ${VIDEO_SEGMENT_MAX_FILE_SIZE}MB, then give it another go.`
      )
    );
  }

  function handleConfirm() {
    if (selectedUrl) {
      const duration = durations[selectedUrl] ?? 0;

      if (onVideoSelect) {
        onVideoSelect(selectedUrl, duration);
      } else if (type !== undefined) {
        const field = type === AddSegmentType.INTRO ? 'intro' : 'outro';
        updateIntroOutro(clipId, field, { url: selectedUrl, duration });
      }
    }
    onClose();
  }

  return (
    <MediaPickerModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      onConfirm={handleConfirm}
      confirmDisabled={confirmDisabled}
    >
      {!showUploadsOnly && (
        <MediaPickerTabBar
          tabs={MEDIA_PICKER_TABS}
          activeTabId={activeTab}
          onTabChange={id => {
            setActiveTab(id as 'uploads' | 'brandkit');
            setSelectedUrl(undefined);
          }}
        />
      )}

      {activeTab === 'uploads' && (
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-3">
            <p className="text-sm font-medium text-slate-700">Upload custom intro/outro videos</p>
            <FilesInput
              onFilesUpload={handleUpload}
              accept={BRAND_KIT_ACCEPT_FORMATS.introOutro}
              multiple={false}
              icon="IconCloudUpload"
              title="Upload Video"
              label="Click to browse or drag and drop a video."
              rootClassNames="h-[155px]"
            />
          </div>

          {recentlyUsedVideos.length > 0 && (
            <div className="flex flex-col gap-3">
              <p className="text-sm font-medium text-slate-700">Recently used</p>
              <div data-testid="recently-used-videos-grid" className="grid grid-cols-3 gap-4">
                {recentlyUsedVideos.map(({ url, name, deletable }) => (
                  <BrandKitMediaCard
                    key={url}
                    url={url}
                    isSelected={selectedUrl === url}
                    onClick={() => setSelectedUrl(url)}
                    type="video"
                    onDelete={deletable ? () => deleteItem(name) : undefined}
                    onDurationAvailable={duration => handleDurationAvailable(url, duration)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'brandkit' && (
        <div className="flex flex-col gap-4">
          {!brandKits.length ? (
            <MediaEmptyState
              icon="IconPalette"
              title="No brand kit available"
              description="Create a brand kit to apply your brand's colors, fonts, assets, voice etc to your edited content."
              ctaLabel="Add Brand Kit"
              ctaHref="/brand-kit"
            />
          ) : (
            <>
              <div className="w-[213px]">
                <BrandKitSelector selectedBrandKitId={activeBrandKitId} onBrandKitChange={handleBrandKitChange} />
              </div>

              {!brandKitVideos.length ? (
                <MediaEmptyState
                  icon="IconMovie"
                  title="No videos added"
                  description="Add intro/outro videos to your brand kit to reuse them across assets."
                  ctaLabel="Add Videos"
                  ctaHref={activeBrandKitId ? `/brand-kit/${activeBrandKitId}/edit` : '/brand-kit'}
                />
              ) : (
                <div data-testid="brandkit-videos-grid" className="grid grid-cols-3 gap-4">
                  {brandKitVideos.map(({ url, originalName }) => (
                    <BrandKitMediaCard
                      key={url}
                      url={url}
                      label={originalName}
                      isSelected={selectedUrl === url}
                      onClick={() => setSelectedUrl(url)}
                      type="video"
                      onDurationAvailable={duration => handleDurationAvailable(url, duration)}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </MediaPickerModal>
  );
}

export default memo(IntroOutroModal);
